import React, { useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './RegisterDeviceModal.module.css';
import { SwapMainAppContext } from '../../../context/SwapMainAppContext';
import { PRISMAX_BACKEND_URL } from '../../../configs/config';

const DEVICE_TYPES = [
    'Bimanual / dual-arm system',
    'Ego data-capture device',
    'Humanoid',
    'Robot arm',
    'Teleoperation station',
    'Other',
];

const SERIAL_MAX_LENGTH = 50;
const EXAMPLES_MAX_COUNT = 5;

// The manufacturer declares their own serial format — either a template
// (A = letter, 0 = digit, other characters literal) or a regex. We convert
// the declared format into a matcher, then check their example serials
// against it, mirroring what the backend will enforce later on.
function buildSerialMatcher(format) {
    const trimmed = format.trim();
    if (!trimmed) return null;

    const looksLikeRegex = /[\^$\\[\]{}+*?|()]/.test(trimmed);
    if (looksLikeRegex) {
        try {
            return new RegExp(trimmed.startsWith('^') ? trimmed : `^${trimmed}$`);
        } catch {
            return 'invalid';
        }
    }

    const escapeChar = (char) => char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    let pattern = '^';
    for (const char of trimmed) {
        if (char === 'A' || char === 'a') pattern += '[A-Za-z]';
        else if (char === '0') pattern += '\\d';
        else pattern += escapeChar(char);
    }
    pattern += '$';

    try {
        return new RegExp(pattern);
    } catch {
        return 'invalid';
    }
}

function ValidationChip({ state }) {
    if (!state) return null;
    const isOk = state.type === 'ok';
    return (
        <div className={`${styles.validChip} ${isOk ? styles.validChipOk : styles.validChipBad}`}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                {isOk ? (
                    <path d="M2 6.5l2.5 2.5L10 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                ) : (
                    <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                )}
            </svg>
            {state.message}
        </div>
    );
}

function DeviceTypeSelect({ value, onChange }) {
    const [isOpen, setIsOpen] = useState(false);
    const wrapRef = React.useRef(null);

    useEffect(() => {
        function handleOutsideClick(event) {
            if (wrapRef.current && !wrapRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        if (isOpen) {
            document.addEventListener('mousedown', handleOutsideClick);
        }
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [isOpen]);

    useEffect(() => {
        function handleKeyDown(event) {
            if (event.key === 'Escape' && isOpen) {
                event.stopPropagation();
                setIsOpen(false);
            }
        }
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen]);

    function handleSelect(type) {
        onChange(type);
        setIsOpen(false);
    }

    return (
        <div className={styles.ddsWrap} ref={wrapRef}>
            <button
                type="button"
                className={`${styles.ddsTrigger} ${isOpen ? styles.ddsTriggerOpen : ''}`}
                onClick={() => setIsOpen((prev) => !prev)}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                <span className={value ? styles.ddsTriggerValue : styles.ddsTriggerPlaceholder}>
                    {value || 'Select a type…'}
                </span>
                <svg
                    className={`${styles.ddsChev} ${isOpen ? styles.ddsChevOpen : ''}`}
                    width="11" height="6" viewBox="0 0 11 6" fill="none"
                >
                    <path d="M1 1l4.5 4L10 1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>

            {isOpen && (
                <ul className={styles.ddsPanel} role="listbox">
                    {DEVICE_TYPES.map((type) => (
                        <li
                            key={type}
                            role="option"
                            aria-selected={value === type}
                            className={`${styles.ddsOption} ${value === type ? styles.ddsOptionSelected : ''}`}
                            onMouseDown={() => handleSelect(type)}
                        >
                            {type}
                            {value === type && (
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                    <path d="M2 6.5l2.5 2.5L10 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function RegisterDeviceModal({ onClose, onSubmit = () => {} }) {
    const [manufacturer, setManufacturer] = useState('');
    const [model, setModel] = useState('');
    const [deviceType, setDeviceType] = useState('');
    const [serialFormat, setSerialFormat] = useState('');
    const [examples, setExamples] = useState('');
    const [specUrl, setSpecUrl] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { userEmail, gatewayToken, showNotification } = useContext(SwapMainAppContext);

    useEffect(() => {
        if (userEmail) setEmail(userEmail);
    }, [userEmail]);

    useEffect(() => {
        function handleKeyDown(event) {
            if (event.key === 'Escape' && !isSubmitting) onClose();
        }
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose, isSubmitting]);

    const serialMatcher = useMemo(() => buildSerialMatcher(serialFormat), [serialFormat]);

    const serialChip = useMemo(() => {
        if (!serialFormat.trim()) return null;
        if (serialFormat.trim().length > SERIAL_MAX_LENGTH) {
            return { type: 'bad', message: `Format must be ${SERIAL_MAX_LENGTH} characters or fewer` };
        }
        if (serialMatcher === 'invalid') {
            return { type: 'bad', message: "That doesn't parse as a template or regex" };
        }
        return { type: 'ok', message: 'Format captured' };
    }, [serialFormat, serialMatcher]);

    const examplesChip = useMemo(() => {
        const lines = examples.split('\n').map((line) => line.trim()).filter(Boolean);
        if (!lines.length) return null;
        if (lines.length > EXAMPLES_MAX_COUNT) {
            return { type: 'bad', message: `Maximum ${EXAMPLES_MAX_COUNT} examples allowed` };
        }
        const tooLong = lines.filter((line) => line.length > SERIAL_MAX_LENGTH);
        if (tooLong.length > 0) {
            return { type: 'bad', message: `Each serial must be ${SERIAL_MAX_LENGTH} characters or fewer` };
        }
        if (!serialFormat.trim() || serialMatcher === 'invalid') return null;
        const badLines = lines.filter((line) => !serialMatcher.test(line));
        if (badLines.length === 0) {
            return { type: 'ok', message: `${lines.length} example${lines.length > 1 ? 's' : ''} match${lines.length === 1 ? 'es' : ''} your format` };
        }
        return { type: 'bad', message: `${badLines.length} of ${lines.length} don't match: ${badLines.slice(0, 2).join(', ')}` };
    }, [examples, serialFormat, serialMatcher]);

    const emailChip = useMemo(() => {
        if (!email.trim()) return null;
        const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
        return valid ? null : { type: 'bad', message: 'Enter a valid email address' };
    }, [email]);

    function isFormValid() {
        const exampleLines = examples.split('\n').map((line) => line.trim()).filter(Boolean);
        return (
            manufacturer.trim() &&
            model.trim() &&
            deviceType &&
            serialFormat.trim() &&
            exampleLines.length > 0 &&
            firstName.trim() &&
            lastName.trim() &&
            email.trim()
        );
    }

    const canSubmit = isFormValid() && serialChip?.type !== 'bad' && examplesChip?.type !== 'bad' && emailChip?.type !== 'bad' && !isSubmitting;

    async function handleSubmit() {
        if (isSubmitting) return;

        if (!isFormValid()) {
            showNotification('warning', 'Please fill in all required fields.');
            return;
        }

        if (serialChip?.type === 'bad' || examplesChip?.type === 'bad') {
            showNotification('warning', 'Please fix your serial number format before submitting.');
            return;
        }

        const payload = { manufacturer, model, deviceType, serialFormat, examples, specUrl, firstName, lastName, email };

        setIsSubmitting(true);
        try {
            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/partner-device/request-listing`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${gatewayToken}`,
                },
                body: JSON.stringify(payload),
            });
            const result = await response.json();

            if (!response.ok || !result.success) {
                showNotification('error', result.msg || 'Something went wrong. Please try again.', 8000);
                return;
            }

            onSubmit(payload);
            showNotification('success', `Thanks for registering your device. Our team is reviewing your listing. We'll be in touch with you at ${result.email}.`, 12000);
            onClose();
        } catch (error) {
            showNotification('error', 'An error occurred. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    }

    function handleOverlayClick(event) {
        if (event.target === event.currentTarget && !isSubmitting) onClose();
    }

    return createPortal(
        <div className={styles.overlay} onClick={handleOverlayClick}>
            <div className={styles.modal}>
                <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close" disabled={isSubmitting}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                    </svg>
                </button>

                <div className={styles.modalHeader}>
                    <div className={styles.formHead}>
                        <h2>List your device</h2>
                        <p>Tell us about the device you&apos;d like to sell in the fleet.</p>
                    </div>
                </div>

                <div className={styles.modalBody}>
                <div className={styles.field}>
                    <div className={styles.fieldLabel}>
                        <span className={styles.lbl}>Manufacturer<span className={styles.req}>*</span></span>
                    </div>
                    <input
                        className={styles.input}
                        type="text"
                        placeholder="e.g. your company or brand name"
                        value={manufacturer}
                        onChange={(e) => setManufacturer(e.target.value)}
                    />
                    <div className={styles.fieldHint}>The brand operators will see on the fleet listing.</div>
                </div>

                <div className={styles.twoCol}>
                    <div className={styles.field}>
                        <div className={styles.fieldLabel}>
                            <span className={styles.lbl}>Model name<span className={styles.req}>*</span></span>
                        </div>
                        <input
                            className={styles.input}
                            type="text"
                            placeholder="e.g. the product / model name"
                            value={model}
                            onChange={(e) => setModel(e.target.value)}
                        />
                    </div>
                    <div className={styles.field}>
                        <div className={styles.fieldLabel}>
                            <span className={styles.lbl}>Device type<span className={styles.req}>*</span></span>
                        </div>
                        <DeviceTypeSelect value={deviceType} onChange={setDeviceType} />
                    </div>
                </div>

                <div className={styles.field}>
                    <div className={styles.fieldLabel}>
                        <span className={styles.lbl}>Serial number format<span className={styles.req}>*</span></span>
                    </div>
                    <input
                        className={styles.input}
                        type="text"
                        placeholder="e.g. AA-0000-0000 or ^[A-Z]{2}\d{8}$"
                        value={serialFormat}
                        onChange={(e) => setSerialFormat(e.target.value)}
                    />
                    <ValidationChip state={serialChip} />
                    <div className={styles.fieldHint}>
                        Use <code>A</code> for a letter, <code>0</code> for a digit, and type any fixed
                        characters as-is — or enter a regex. We use this to verify operators&apos; serials
                        are genuinely yours.
                    </div>
                </div>

                <div className={styles.field}>
                    <div className={styles.fieldLabel}>
                        <span className={styles.lbl}>Example serial numbers<span className={styles.req}>*</span></span>
                        <span className={styles.opt}>2–3 real units</span>
                    </div>
                    <textarea
                        className={styles.input}
                        rows={3}
                        placeholder={'One per line, e.g.\nAC-2026-0457\nAC-2026-0458'}
                        value={examples}
                        onChange={(e) => setExamples(e.target.value)}
                    />
                    <ValidationChip state={examplesChip} />
                    <div className={styles.fieldHint}>Real serials we&apos;ll check against your format. Never shown publicly.</div>
                </div>

                <div className={styles.field}>
                    <div className={styles.fieldLabel}>
                        <span className={styles.lbl}>Spec sheet or product URL</span>
                        <span className={styles.opt}>Optional</span>
                    </div>
                    <input
                        className={styles.input}
                        type="text"
                        placeholder="https://…"
                        value={specUrl}
                        onChange={(e) => setSpecUrl(e.target.value)}
                    />
                    <div className={styles.fieldHint}>Helps us review faster and gives operators specs at a glance.</div>
                </div>

                <div className={styles.sectionDivider}>
                    <div className={styles.sectionLabel}>Contact details</div>
                    <div className={styles.sectionNote}>So we can follow up about your listing. Never shown publicly.</div>
                </div>

                <div className={styles.twoCol}>
                    <div className={styles.field}>
                        <div className={styles.fieldLabel}>
                            <span className={styles.lbl}>First name<span className={styles.req}>*</span></span>
                        </div>
                        <input
                            className={styles.input}
                            type="text"
                            placeholder="e.g. your first name"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                        />
                    </div>
                    <div className={styles.field}>
                        <div className={styles.fieldLabel}>
                            <span className={styles.lbl}>Last name<span className={styles.req}>*</span></span>
                        </div>
                        <input
                            className={styles.input}
                            type="text"
                            placeholder="e.g. your last name"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                        />
                    </div>
                </div>

                <div className={`${styles.field} ${styles.fieldLast}`}>
                    <div className={styles.fieldLabel}>
                        <span className={styles.lbl}>Email<span className={styles.req}>*</span></span>
                    </div>
                    <input
                        className={styles.input}
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        disabled={!!userEmail}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                    <ValidationChip state={emailChip} />
                    {userEmail && (
                        <div className={styles.fieldHint}>This is your account email.</div>
                    )}
                </div>

                </div>

                <div className={styles.formFooter}>
                    <div className={styles.footerBtns}>
                        <button type="button" className={styles.btnGhost} onClick={onClose} disabled={isSubmitting}>
                            Cancel
                        </button>
                        <button type="button" className={styles.btnPrimary} onClick={handleSubmit} disabled={!canSubmit}>
                            {isSubmitting ? 'Submitting…' : (
                                <>
                                    Submit for review
                                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                                        <path d="M3 6.5h7M7 3.5l3 3-3 3" stroke="#111" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}

export default RegisterDeviceModal;
